import { Component, OnInit } from '@angular/core';
import { PersonalDataRequestService } from '../core/services/personal-data-request.service';
import { EmailVerificationModel } from '../core/models/email-verification.model';
import { finalize } from 'rxjs/operators';
import { NotificationsService } from 'angular2-notifications';
import { PublicGdprPortalModel } from '../core/models/public-GDPR-portal.model';
import { PublicPersonalDataRequestModel } from '../core/models/public-personal-data-request.model';
import { AuthenticationService } from '../core/services/authentication.service';
import { RequestAuthentication } from '../core/models/request-authentication.model';
import { ActivatedRoute } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageEnum } from '../core/enums/language-enum';
import { environment } from '@environment';
import { SideNavigationService } from '@sb-tools/typescript/services/side-navigation.service';
import { LeftPanelComponent } from '../left-panel/left-panel.component';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SidenavComponent } from '@sb-tools/typescript/components/sidenav/sidenav.component';

declare let FB: any;

@Component({
    selector: 'app-request',
    templateUrl: './request.component.html',
    styleUrls: ['./request.component.scss'],
    imports: [TranslateModule, LeftPanelComponent, NgClass, FormsModule, SidenavComponent],
})
export class RequestComponent implements OnInit {
    contactEmail: string;
    contactEmailCode: number;
    instagramRedirectUri: string;
    isAddingContactEmail: boolean;
    isAddingNewEmail: boolean;
    isContactEmailAdded: boolean;
    isFormSubmitted: boolean;
    isNewEmailAdded: boolean;
    isRequestSubmitted: boolean;
    isSubmittingRequest: boolean;
    newEmail: string;
    newEmailCode: number;
    portal: PublicGdprPortalModel;
    request: PublicPersonalDataRequestModel;
    sidebarMode = 'push';
    token: string;
    youtubeClientId: string;
    youtubeConnectLink: string;
    youtubeRedirectUri: string;

    constructor(
        public sideNavigationService: SideNavigationService,
        private personalDataRequestService: PersonalDataRequestService,
        private notificationsService: NotificationsService,
        private authenticationService: AuthenticationService,
        private activatedRoute: ActivatedRoute,
        private cookieService: CookieService,
        private translateService: TranslateService,
    ) {
        this.youtubeClientId = environment.youtubeClientId;

        this.instagramRedirectUri = window.location.origin + '/' + 'instagramOAuth';
        this.youtubeRedirectUri = window.location.origin + '/' + 'youtubeOAuth';

        this.portal = activatedRoute.snapshot.data.initData[0];
        this.request = activatedRoute.snapshot.data.initData[1];
        this.setupLanguage(this.portal.language);
    }

    ngOnInit() {
        if (
            window.innerWidth <= 900 ||
            document.documentElement.clientWidth <= 900 ||
            document.body.clientWidth <= 900
        ) {
            this.sidebarMode = 'over';
        } else {
            this.sidebarMode = 'push';
        }

        this.activatedRoute.queryParams.subscribe((params) => {
            this.token = params['r'];

            const authData = new RequestAuthentication();
            authData.portalName = this.portal.name;
            authData.token = this.token;
            this.authenticationService.saveAuthenticationData(authData);

            this.request.authUserInfo.facebookId = this.cookieService.get('facebookOAuthCode' + this.token);
            this.request.authUserInfo.instagramId = this.cookieService.get('instagramOAuthCode' + this.token);
            this.request.authUserInfo.youtubeId = this.cookieService.get('youtubeOAuthCode' + this.token);
        });

        this.youtubeConnectLink =
            'https://accounts.google.com/o/oauth2/v2/auth' +
            '?scope=https://www.googleapis.com/auth/youtube.readonly' +
            '&access_type=offline' +
            '&include_granted_scopes=true' +
            '&state=state_parameter_passthrough_value' +
            '&redirect_uri=' +
            this.youtubeRedirectUri +
            '&response_type=code' +
            '&client_id=' +
            this.youtubeClientId;
    }

    setupLanguage(language) {
        switch (language) {
            case LanguageEnum.nb:
                this.translateService.use('nb');
                break;
            case LanguageEnum.se:
                this.translateService.use('sv_SE');
                break;
            case LanguageEnum.da:
                this.translateService.use('da_DK');
                break;
            default:
                this.translateService.use(undefined);
        }
    }

    addContactEmail(): void {
        this.isContactEmailAdded = false;
        this.isAddingContactEmail = true;
        this.personalDataRequestService
            .addContactAddress(this.request.contactAddress)
            .pipe(
                finalize(() => {
                    this.isAddingContactEmail = false;
                }),
            )
            .subscribe(() => {
                this.isContactEmailAdded = true;
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Verification code was sent to email address'),
                );
            });
    }

    addEmail(): void {
        this.isNewEmailAdded = false;
        this.isAddingNewEmail = true;
        const emailModel = new EmailVerificationModel();
        emailModel.email = this.newEmail;
        this.personalDataRequestService
            .addEmail(emailModel)
            .pipe(
                finalize(() => {
                    this.isAddingNewEmail = false;
                }),
            )
            .subscribe(() => {
                this.isNewEmailAdded = true;
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Verification code was sent to email address'),
                );
            });
    }

    confirmContactEmail(): void {
        this.isAddingContactEmail = true;
        this.personalDataRequestService
            .confirmContactAddress(this.contactEmailCode)
            .pipe(
                finalize(() => {
                    this.isAddingContactEmail = false;
                }),
            )
            .subscribe((data) => {
                this.request.contactAddress.email = data.email;
                this.request.contactAddress.isVerified = data.isVerified;
                this.isContactEmailAdded = false;
                this.contactEmail = '';
                this.contactEmailCode = undefined;
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Verification code was sent to email address'),
                );
            });
    }

    confirmEmail(): void {
        this.isAddingNewEmail = true;
        this.personalDataRequestService
            .confirmEmail(this.newEmail, this.newEmailCode)
            .pipe(
                finalize(() => {
                    this.isAddingNewEmail = false;
                }),
            )
            .subscribe((response) => {
                this.request.authUserInfo.emails.push(response);
                this.isNewEmailAdded = false;
                this.newEmail = '';
                this.newEmailCode = undefined;
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Verification code was sent to email address'),
                );
            });
    }

    isSubmitButtonDisabled(): boolean {
        if (
            (this.request.authUserInfo.facebookId ||
                this.request.authUserInfo.instagramId ||
                this.request.authUserInfo.youtubeId ||
                this.request.authUserInfo.emails.length >= 1) &&
            this.request.contactAddress.isVerified
        ) {
            return false;
        } else {
            return true;
        }
    }

    loginWithFacebook(): void {
        FB.login((response) => {
            if (response.authResponse) {
                FB.api('/me', () => {
                    this.request.authUserInfo.facebookId = response.authResponse.accessToken;
                    this.cookieService.put('facebookOAuthCode' + this.token, response.authResponse.accessToken);
                });
            } else {
                return console.error(response);
            }
        });
    }

    removeEmail(removedEmailModel): void {
        this.personalDataRequestService
            .removeEmail(removedEmailModel.email)
            .pipe(
                finalize(() => {
                    this.isAddingNewEmail = false;
                }),
            )
            .subscribe(() => {
                this.request.authUserInfo.emails = this.request.authUserInfo.emails.filter((emailModel) => {
                    return emailModel.email !== removedEmailModel.email;
                });
            });
    }

    submitRequest(): void {
        if (
            !this.request.contactAddress ||
            !this.request.contactAddress.email ||
            !this.request.contactAddress.isVerified
        ) {
            this.isFormSubmitted = true;
            return;
        } else {
            this.isSubmittingRequest = true;
            this.personalDataRequestService
                .processRequest(this.request)
                .pipe(
                    finalize(() => {
                        this.isSubmittingRequest = false;
                    }),
                )
                .subscribe(() => {
                    this.isRequestSubmitted = true;
                    this.cookieService.remove('instagramOAuthCode' + this.token);
                    this.cookieService.remove('facebookOAuthCode' + this.token);
                    this.cookieService.remove('youtubeOAuthCode' + this.token);
                });
        }
    }
}
